import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { useStatusHistoryCategoryChart } from '@/charts/configs/useStatusHistoryCategoryChart';
import { useStatusHistoryTeamsChart } from '@/charts/configs/useStatusHistoryTeamsChart';
import { useStatusHistoryLocationChart } from '@/charts/configs/useStatusHistoryLocationChart';
import { useStatusHistoryVelocityChart } from '@/charts/configs/useStatusHistoryVelocityChart';
import { useStatusHistoryBurndownChart } from '@/charts/configs/useStatusHistoryBurndownChart';
import { useAppContext } from '@/composables/useAppContext';
import { useItemsGrid } from '@/composables/useItemsGrid';

export default () => {
  const store = useStore();
  const { t } = useI18n();
  const theme = useTheme();
  const settings = computed(() => store.state.settings);
  const locale = computed(() => store.getters.currentLocale);

  const duckDBService = store.getters.getDuckDBService;
  const conn = store.getters.getDBConnection;
  const dataLoaded = ref(false);

  const themeMode = computed(() => {
    return theme.global.name.value === 'dark' ? true : false;
   });


  const timestampCache = ref(new Map());

  const filterStatusHistoryContext = computed(() => store.state.filterStatusHistoryContext);
  const chartFontColor = computed(() => store.getters.chartFontColor);

  // BaseTimeSeriesChart stacks one series per status COLOUR and names each band
  // through `bandTitles`, so the status templates never reach the component: group
  // them by colour here and join the titles, exactly as both old SFCs did inline.
  const bandTitles = computed(() => {
    const titles = {};
    Object.values(store.getters.getStatusTemplatesObj || {}).forEach((tpl) => {
      if (!tpl?.color) return;
      titles[tpl.color] = titles[tpl.color] ? `${titles[tpl.color]}, ${tpl.title}` : tpl.title;
    });
    return titles;
  });

  // Page-owned level ref: the category chart's IssueTemplate short-circuit writes
  // to it to skip the subitem level
  const activityCategoryLevel = ref(0);

  const activityCategoryChart = useStatusHistoryCategoryChart({
    chartName: 'statusHistoryCategoryStackedBarChart',
    filterContext: filterStatusHistoryContext,
    currentLevel: activityCategoryLevel,
    conn
  });

  const activityTeamsChart = useStatusHistoryTeamsChart({
    chartName: 'statusHistoryTeamsStackedBarChart',
    filterContext: filterStatusHistoryContext,
    conn
  });

  const activityLocationChart = useStatusHistoryLocationChart({
    chartName: 'statusHistoryLocationStackedBarChart',
    filterContext: filterStatusHistoryContext,
    conn
  });

  // ActivityPage owns a LOCAL dataLoaded (its parquet loads lazily), so the grid
  // gets that ref rather than useAppContext's global store flag.
  const app = useAppContext();
  const grid = useItemsGrid({
    ...app,
    dataLoaded,
    filterContext: filterStatusHistoryContext,
    itemsView: 'filteredItemsByStatusHistory',
    t
  });

  const velocityChart = useStatusHistoryVelocityChart({ conn });
  const burndownChart = useStatusHistoryBurndownChart({ conn });

  const startDate = ref('');
  const endDate = ref('');
  const dateRange = ref([]);
  const minDate = ref(new Date());
  const maxDate = ref(new Date());
  const showNoActivityWarning = ref(false);

  // Create a composable for data fetching state
  const useDataFetching = () => {
    const loading = ref(true);
    const progressValue = ref(0);
    let progressInterval = null;

    const startProgress = () => {
      clearInterval(progressInterval);
      progressValue.value = 0;
      progressInterval = setInterval(() => {
        if (progressValue.value < 70) {
          progressValue.value += 10;
        }
      }, 500);
    };

    const stopProgress = () => {
      clearInterval(progressInterval);
      progressValue.value = 100;
    };

    onUnmounted(() => {
      clearInterval(progressInterval);
    });

    return {
      loading,
      progressValue,
      startProgress,
      stopProgress
    };
  };

  const { loading, progressValue, startProgress, stopProgress } = useDataFetching();



  const handleDateRangeCleared = async () => {
    // Clear the dateRange value
    dateRange.value = [];

    // Remove the date range filter to show all data
    if (filterStatusHistoryContext.value) {
      await filterStatusHistoryContext.value.removeFilter('activityDatePicker');
    }
  };

  const handleDateRangeChange = async (newRange) => {
    // If the range is cleared (null or empty), handle it
    if (!newRange || !Array.isArray(newRange) || newRange.length === 0) {
      await handleDateRangeCleared();
      return;
    }

    if (newRange.length !== 2) {
      return;
    }

    const newStart = toDate(newRange[0]);
    const newEnd = toDate(newRange[1]);

    if (!newStart || !newEnd) {
      return;
    }

    // Update dateRange immediately
    dateRange.value = newRange;

    // Format dates for SQL query (YYYY-MM-DD format)
    const formattedStartDate = formatDateForSQL(newStart);
    const formattedEndDate = formatDateForSQL(newEnd);

    // Generate SQL query for filtering - only include status records where status CHANGED during the date range
    const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    WHERE sh.validFrom >= '${formattedStartDate}'
    AND sh.validFrom <= '${formattedEndDate}'
  `;

    // Pass the query to the filter context
    filterStatusHistoryContext.value.updateFilterWithQuery('activityDatePicker', query);
  };

  // Helper function to format date for SQL query
  const formatDateForSQL = (date) => {
    const validDate = toDate(date);
    if (!validDate) return '';
  
    const year = validDate.getFullYear();
    const month = String(validDate.getMonth() + 1).padStart(2, '0');
    const day = String(validDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return new Date(value);
    return null;
  };

  // The raw status history is fetched lazily (first visit only) through the data
  // loader; this catalog check only picks between the first-visit path (getData)
  // and the revisit path in initializeData below
  const checkStatusHistoryExists = () => duckDBService.tableExists('status_history');

  const getData = async () => {
    if (!settings.value.projectCode) return;

    try {
      loading.value = true;
      dataLoaded.value = false;
      startProgress();

      // The manifest's lazy `status_history` entry (src/data/manifest.js): fetched once,
      // then its postLoad derives status_history_temporal. A repeat call (token rotation
      // re-runs getData) is a no-op, and a failed fetch is retried on the next call.
      await app.loader.ensure('status_history');

      // Query to find min and max dates in the status_history table
      const dateRangeResult = await conn.query(`
      SELECT 
        MIN(createdDate) as minDate,
        MAX(createdDate) as maxDate
      FROM status_history
    `);
    
      const dateRangeData = dateRangeResult.toArray();
    
      if (dateRangeData && dateRangeData.length > 0) {
        // Normalize to day boundaries: the picker validates selections against
        // min/max including time-of-day. The range always extends to the end of
        // today regardless of when the last status record was created
        minDate.value = new Date(dateRangeData[0].minDate);
        minDate.value.setHours(0, 0, 0, 0);
        maxDate.value = new Date();
        maxDate.value.setHours(23, 59, 59, 999);

        // If dateRange is not set, initialize it with last 61 days up to today
        if (!dateRange.value || dateRange.value.length !== 2) {
          const today = new Date();
          const last61Days = new Date(today);
          last61Days.setDate(last61Days.getDate() - 61);

          // Use the later of minDate or 61 days ago
          const defaultStartDate = last61Days > minDate.value ? last61Days : minDate.value;

          dateRange.value = [defaultStartDate, today];
          startDate.value = formatDateForPicker(defaultStartDate);
          endDate.value = formatDateForPicker(today);
        }
      }

      await store.state.filterStatusHistoryContext.initializeFilterContext();

      // Apply the initial date range filter
      if (dateRange.value && dateRange.value.length === 2) {
        await handleDateRangeChange(dateRange.value);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      dataLoaded.value = true;
      stopProgress();
      requestAnimationFrame(() => {
          loading.value = false;      
      });
    }
  };

  const getDateFormat = (l) => {
    switch (l) {
      case 'en':
        return 'MM/dd/yyyy';
      case 'en-GB':
      case 'en-AU':
        return 'dd/MM/yyyy';
      case 'fr':
        return 'dd/MM/yyyy';
      case 'it':
        return 'dd/MM/yyyy';
      case 'zh-Hans':
      case 'zh-Hant':
        return 'yyyy/MM/dd';
      case 'nl':
        return 'dd-MM-yyyy';
      case 'pt-BR':
        return 'dd/MM/yyyy';
      case 'vi':
        return 'dd/MM/yyyy';
      default:
        return 'dd/MM/yyyy';
    }
  };

  const formatDateForPicker = (date) => {
    const validDate = toDate(date);
    if (!validDate) return '';
  
    const year = validDate.getFullYear();
    const month = String(validDate.getMonth() + 1).padStart(2, '0');
    const day = String(validDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(locale.value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const stopWatchers = [
    watch(
      () => [settings.value.scopedToken, settings.value.projectCode],
      ([newScopedToken, newProjectCode], oldValue) => {
        if (newScopedToken && newProjectCode && 
            JSON.stringify([newScopedToken, newProjectCode]) !== JSON.stringify(oldValue)) {
          getData();
        }
      },
      { deep: true }
    ),
  ];

  // Initialize data on component mount only if necessary
  const initializeData = async () => {
    if (settings.value.scopedToken && settings.value.projectCode) {
      // First check if the status_history table already exists
      const statusHistoryExists = await checkStatusHistoryExists();
    
      if (!statusHistoryExists) {
        // Only fetch data if the table doesn't exist
        await getData();

      } else {
        // If data already exists, initialize filter context and apply date range before marking as loaded
        loading.value = false;

        await store.commit('resetStatusHistoryFilters');

        // Still need to query for min and max dates even if the table exists
        try {
          const dateRangeResult = await conn.query(`
          SELECT
            MIN(createdDate) as minDate,
            MAX(createdDate) as maxDate
          FROM status_history
        `);

          const dateRangeData = dateRangeResult.toArray();

          if (dateRangeData && dateRangeData.length > 0) {
            // Normalize to day boundaries like getData() does: the picker
            // validates selections against min/max including time-of-day. The
            // range always extends to the end of today regardless of when the
            // last status record was created
            minDate.value = new Date(dateRangeData[0].minDate);
            minDate.value.setHours(0, 0, 0, 0);
            maxDate.value = new Date();
            maxDate.value.setHours(23, 59, 59, 999);

            // Set initial date range to last 61 days up to today if not already set
            if (!dateRange.value || dateRange.value.length !== 2) {
              const today = new Date();
              const last61Days = new Date(today);
              last61Days.setDate(last61Days.getDate() - 61);

              // Use the later of minDate or 61 days ago
              const defaultStartDate = last61Days > minDate.value ? last61Days : minDate.value;

              dateRange.value = [defaultStartDate, today];
              startDate.value = formatDateForPicker(defaultStartDate);
              endDate.value = formatDateForPicker(today);

              // Apply the initial date range filter BEFORE marking data as loaded
              await handleDateRangeChange(dateRange.value);
            }
          }
        } catch (error) {
          console.error("Error fetching date range:", error);
        }

        // Mark as loaded AFTER applying the date range filter
        dataLoaded.value = true;
      }
    }
  };

  onMounted(() => {
    initializeData();
  });

  // Cleanup watchers and cache when component is unmounted
  onUnmounted(() => {
    try {
      stopWatchers.forEach(unwatch => unwatch());
      timestampCache.value.clear();

      // Reset all filters so they don't persist to other pages
      if (filterStatusHistoryContext.value) {
        filterStatusHistoryContext.value.resetFilters().catch(err => {
          console.error('Error resetting filters on unmount:', err);
        });
      }
    } catch (error) {
      console.error('Error in onUnmounted:', error);
    }
  });

  return {
    locale,
    dataLoaded,
    themeMode,
    filterStatusHistoryContext,
    chartFontColor,
    bandTitles,
    activityCategoryLevel,
    activityCategoryChart,
    activityTeamsChart,
    activityLocationChart,
    grid,
    velocityChart,
    burndownChart,
    dateRange,
    minDate,
    maxDate,
    showNoActivityWarning,
    loading,
    progressValue,
    handleDateRangeCleared,
    handleDateRangeChange,
    getDateFormat,
    formatDateForDisplay
  };
};
